import {useEffect, useRef, useState} from 'react';
import {Swiper, SwiperSlide} from 'swiper/react';
import SwiperCore, {Navigation} from "swiper";
import {Link} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import Skeleton from 'react-loading-skeleton';
import {getSliderItems} from '../../api/apiClient';
import {useAuth} from '../../context/AuthContext';
import SlideCard from './slide-card/SlideCard';
import useWindowSize from '../../hooks/useWindowSize';
import 'swiper/css';
import Classes from './GSlider.module.scss';
import styles from '../../pages/artist/Artist.module.scss';

import rightIcon from '../../assets/icons/right.svg';
import leftIcon from '../../assets/icons/left.svg';

type gSlider = {
    apiPath: string,
    limit: number,
    sticker: boolean,
    mainTitle: string,
    linkTitle: string,
    link: string,
    apiPathResName: any,
    userSticker: boolean
}

const GSlider = (props: any) => {
    const {mainTitle, link, apiPath, limit, sticker, linkTitle, apiPathResName, userSticker}: gSlider = props;
    const {width} = useWindowSize();

    const {getToken} = useAuth();
    const token = getToken();

    //== Swiper Slides
    const [isBeginning, setIsBeginning] = useState<boolean>(true);
    const [isEnd, setIsEnd] = useState<boolean>(false);
    const swiperRef = useRef<SwiperCore>();

    const {
        data,
        isError,
        isLoading,
        isFetching
    } = useQuery([apiPath, 'gSlider'], () => getSliderItems(apiPath, limit, token), {
        useErrorBoundary: true
    });

    let datName: any = apiPathResName === 'categories' ? data?.categories : data?.resources;
    useEffect(() => {
        let mount: boolean = true;
        if (mount) {
            if (datName?.length <= 4) {
                setIsEnd(true);
            }
        }
        return (): void => {
            mount = false;
        }
    }, [datName]);

    let content = null;
    if (isLoading || isFetching) {
        content = (
            <div className={Classes.gSliderWrap}>
                <div className={`${Classes.titleLink} d-flex align-items-center justify-content-between`}>
                    <h2 className={'m-0 mt-1'}>{mainTitle}</h2>
                    <Link to={link}>See all {linkTitle}</Link>
                </div>

                <ul className={`${Classes.gSliderPlaceholder} ${(sticker || userSticker) ? Classes.sickerLoader : ''}`}>
                    {Array(width <= 576 ? 3 : width <= 992 ? 4 : width <= 1199 ? 5 : 6).fill(1).map((_, i) => (
                        <li key={i}>
                            <Skeleton style={{marginBottom: '10px'}}/>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    if (!isLoading && isError) {
        content = (
            <div className={`${styles.notFoundErrorBox} d-inline-flex justify-content-center mt-5`}>
                <h2 className={'d-inline-block'}>
                    Something went wrong! Back <Link to={'/'}>Home</Link>
                </h2>
            </div>
        );
    }

    if (!isLoading && !isError && datName?.length > 0) {
        content = (
            <div className={Classes.gSliderWrap}>
                <div className={`${Classes.titleLink} d-flex align-items-center justify-content-between`}>
                    <h2 className={'m-0 mt-1'}>{mainTitle}</h2>
                    {datName?.length >= 2 ? (<Link to={link} className={'m-0 mt-1'}>See all {linkTitle}</Link>) : null}
                </div>

                <div className={Classes.swiperWrap}>
                    <Swiper
                        spaceBetween={15}
                        slidesPerGroup={sticker ? 6 : userSticker ? 4 : 5}
                        slidesPerView={sticker ? 5.3 : userSticker ? 3.3 : 4.3}
                        modules={[Navigation]}
                        onBeforeInit={(swiper: any) => {
                            swiperRef.current = swiper;
                        }}
                        onSlideChange={(swiper: SwiperCore) => {
                            setIsEnd(swiper.isEnd);
                            setIsBeginning(swiper.isBeginning);
                        }}
                        breakpoints={{
                            320: {
                                slidesPerGroup: sticker || userSticker ? 3 : 2,
                                slidesPerView: sticker || userSticker ? 3.3 : 2.3,
                                spaceBetween: 6
                            },
                            768: {
                                slidesPerGroup: sticker || userSticker ? 4 : 3,
                                slidesPerView: sticker || userSticker ? 4.3 : 3.3,
                                spaceBetween: 12
                            },
                            1200: {
                                slidesPerGroup: sticker ? 6 : userSticker ? 4 : 5,
                                slidesPerView: sticker ? 6.3 : userSticker ? 4.3 : 5.3
                            }
                        }}
                    >
                        {datName?.map((slide: any, i: number) => (
                            <SwiperSlide key={i}>
                                <SlideCard
                                    imgSrc={slide?.thumb || slide?.filePath}
                                    filePath={slide?.filePath}
                                    title={slide?.name || slide?.title}
                                    sticker={sticker || userSticker}
                                    slug={slide?.slug}
                                    {...slide}
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                    <div className={'d-none d-md-block'}>
                        <button
                            onClick={() => swiperRef.current?.slidePrev()}
                            className={`${Classes.prevBtn} ${isBeginning ? 'd-none' : 'd-flex'}`}
                        >
                            <img src={leftIcon} alt="Left" className={'img-fluid'}/>
                        </button>
                        <button
                            onClick={() => swiperRef.current?.slideNext()}
                            className={`${Classes.nextBtn} ${isEnd ? 'd-none' : 'd-flex'}`}
                        >
                            <img src={rightIcon} alt="Right" className={'img-fluid'}/>
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <>
            {content}
        </>
    );
};

export default GSlider;